import { IS_DEV } from "@constants/env";
import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { isSafeUrl } from "@memorang/helpers/common";
import type { Plan, ProductType } from "@memorang/types";
import CustomSnackbar from "@memorang/ui/components/CustomSnackbar";
import { getExternalPurchaseUrl } from "@memorang/ui/components/IAPModal/external-purchase-urls";
import { router } from "expo-router";
import { useState } from "react";
import { purchaseUrlMap } from "../../constants";
import RestorePurchaseContent from "../IAPModal/RestorePurchase/RestorePurchaseContent";
import PurchaseContainerBase from "./PurcahaseContainerBase";

const PurchaseContainer = ({
	handleClose,
	productType,
	fromHomePage,
	explicitProductNumericId,
	explicitProductId,
}: {
	handleClose: () => void;
	productType: ProductType;
	fromHomePage?: boolean;
	explicitProductNumericId?: number;
	explicitProductId?: string;
}) => {
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const [showError, setShowError] = useState(false);
	const [showRestorePurchaseDialog, setShowRestorePurchaseDialog] =
		useState(false);

	const handleShowRestorePurchaseDialog = () => {
		setShowRestorePurchaseDialog(true);
	};

	const handlePurchase = (
		productNumericId: number,
		productId: string,
		selectedPlan?: Plan,
	) => {
		handleClose?.();
		if (!productNumericId) {
			return;
		}

		const externalPurchaseUrl = getExternalPurchaseUrl({
			productId: productNumericId,
			selectedPlan,
			isDev: IS_DEV,
		});
		if (externalPurchaseUrl) {
			if (!isSafeUrl(externalPurchaseUrl, { allowedSchemes: ["https"] })) {
				return;
			}
			window.open(externalPurchaseUrl, "_blank", "noopener,noreferrer");
			return;
		}

		const customPurchaseUrl = purchaseUrlMap[productNumericId]?.purchaseUrl;
		if (customPurchaseUrl) {
			if (!isSafeUrl(customPurchaseUrl, { allowedSchemes: ["https"] })) {
				return;
			}
			window.open(customPurchaseUrl, "_blank", "noopener,noreferrer");
		} else {
			explicitCallTrackCustomEvent({
				eventName: events.paymentSubmitted,
				planId: selectedPlan?.id,
				product_id: productId,
				plan_amount: selectedPlan?.amount,
				plan_duration: selectedPlan?.duration,
				plan_interval: selectedPlan?.interval,
				plan_currency: selectedPlan?.currency,
			});
			router.push({
				pathname: "/(protected)/purchase/[id]/checkout",
				params: {
					id: productNumericId,
					planId: selectedPlan?.id,
					productType: productType,
				},
			});
		}
	};

	return (
		<>
			{!showRestorePurchaseDialog && (
				<PurchaseContainerBase
					handleClose={handleClose}
					fromHomePage={fromHomePage}
					productType={productType}
					handlePurchase={handlePurchase}
					handleShowRestorePurchaseDialog={handleShowRestorePurchaseDialog}
					purchaseInProgress={false}
					explicitProductId={explicitProductId}
					explicitProductNumericId={explicitProductNumericId}
				/>
			)}
			{showRestorePurchaseDialog && (
				<RestorePurchaseContent
					handleDismiss={() => setShowRestorePurchaseDialog(false)}
					handleClose={handleClose}
				/>
			)}
			{showError && (
				<CustomSnackbar
					handleDismiss={() => setShowError(false)}
					visible={showError}
					duration={2000}
					message="Something went wrong while creating a checkout, try again"
				/>
			)}
		</>
	);
};

export default PurchaseContainer;
